const GOVERNED_SOURCE_DIGEST_ID = 'governed_graph_reader_lib' as const;
void GOVERNED_SOURCE_DIGEST_ID;

/**
 * §5 Governed graph-reader — the first *generalized* (rule-driven) reader over the
 * typed graph projection. It implements the load-bearing GR-C discipline:
 *
 *   STRUCTURE FROM GRAPH, CONTENT FROM LAKE.
 *
 * The reader returns only the *shape* of a chain — which typed edges exist and the
 * source artifact that cites each — and NEVER a metric value. Every number on a
 * rendered chain is fetched separately, live from the lake / an approved-engine
 * leaf (see the evaluator's `executeApprovedLeaf` port). A stale *structural* edge
 * is recoverable (it points at an artifact the lake re-reads live); a stale
 * *number* would be a governance failure — which cannot happen here because this
 * module carries no numbers to go stale.
 *
 * v1 realizes the *structure* read via **(B) live-reprojection**: at query time the
 * touched subgraph is reprojected from the live relational bindings
 * (`StrategicObjectiveBinding` ⋈ `InitiativeObjectiveBinding` on
 * `(tenantId, strategyId, objectiveType)`, plus the realization-commitment DTOs and
 * the objective owner) into the SAME typed-edge shape the graph-projection writer
 * emits, carrying the same citations. Reprojection cannot be staler than the
 * materialized graph, so it removes the only remaining staleness surface (a missing
 * just-created edge). Realization **(A)** (reading the materialized projection
 * directly) becomes legal once the projection carries a `sourceGeneration` stamp
 * this reader can gate on — until then materialized reads MUST fail closed
 * `stale_structure`. Charter §5.1 / §5.2.
 *
 * This module is deliberately pure (no Prisma, no I/O): the apps/api concrete
 * reader supplies the relational rows and this module reprojects + fences them, so
 * the whole freshness contract is unit-testable without a database.
 */
import type { OntologyRelationshipKindDto } from './ontology';
import type { DecisionLedgerRealizationCommitmentOwnerDto } from './decision-ledger';
import type {
  InferenceBindings,
  InferenceEdgeLiteral,
  InferenceEvaluationScope,
  InferenceReadResult,
} from './symbolic-inference';

/** Scope every structural hop is pinned to (charter §5.2 as-of pinning). */
export type GovernedGraphReaderScope = Readonly<{
  tenantId: string;
  strategyId: string;
  /** The tenant's `latest_approved` as-of; every hop's structure is stamped with it. */
  asOf: string;
  industryProfileId?: string;
  companyMappingId?: string;
}>;

export type GovernedGraphNodeKind =
  | 'metric'
  | 'objective'
  | 'initiative'
  | 'commitment'
  | 'owner';

/**
 * A typed structural edge. Carries citation + as-of + (optional) source generation.
 * It NEVER carries a metric value — content is read live from the lake by the
 * engine leaves. This is the shape the graph-projection writer emits, so reader and
 * writer stay unified on one schema and the later switch to realization (A) is a
 * source swap, not a contract change (charter §5.1).
 */
export type GovernedGraphEdge = Readonly<{
  fromId: string;
  toId: string;
  edgeType: OntologyRelationshipKindDto;
  fromKind: GovernedGraphNodeKind;
  toKind: GovernedGraphNodeKind;
  /** The source artifact that authorizes this edge (the citation carried to render). */
  sourceArtifactRef: string;
  /** Exact live relational binding row that authorized this edge. */
  sourceBindingId: string;
  /** Approval timestamp read from the source row; never synthesized from query scope. */
  sourceApprovedAt: string | null;
  /** Live IOB allocation used only as cited RF2 content; the evaluator never calculates it. */
  weightPct?: number;
  ownerPosition?: DecisionLedgerRealizationCommitmentOwnerDto['ownerPosition'];
  /**
   * Generation of the *source* the edge was reprojected from. Under live
   * reprojection (B) this is the source generation itself, so structure is as
   * fresh as the source by construction. Under materialized reads (A) the reader
   * compares it against the projection generation and fails `stale_structure` on
   * divergence.
   */
  sourceGeneration: string | null;
  asOf: string;
  scope: GovernedGraphReaderScope;
}>;

/** Relational rows the concrete reader supplies (mirrors the DB tables). */
export type StrategicObjectiveBindingRow = Readonly<{
  id: string;
  tenantId: string;
  strategyId: string;
  asOf: string;
  objectiveType: string;
  metricKey: string;
  ownerUserId: string | null;
  /** The binding's approval stamp — the edge citation's as-of when present. */
  approvedAt: string | null;
  sourceGeneration?: string | null;
}>;

export type InitiativeObjectiveBindingRow = Readonly<{
  id: string;
  tenantId: string;
  strategyId: string;
  asOf: string;
  approvedAt: string | null;
  objectiveType: string;
  initiativeId: string;
  weightPct: number;
  sourceGeneration?: string | null;
}>;

export type RealizationCommitmentRow = Readonly<{
  id: string;
  tenantId: string;
  strategyId: string;
  asOf: string;
  approvedAt: string | null;
  commitmentId: string;
  /** The metric this commitment MOVES. */
  metricKey: string;
  /** Optional governed initiative identity; absent preserves legacy metric-only commitments. */
  initiativeId?: string | null;
  /** Explicit identity from this commitment's approved owner binding, never an owner hint. */
  ownerUserId?: string | null;
  ownerPosition?: DecisionLedgerRealizationCommitmentOwnerDto['ownerPosition'];
  sourceArtifactRef: string;
  sourceGeneration?: string | null;
}>;

export type GovernedGraphReprojectionInput = Readonly<{
  strategicObjectiveBindings: readonly StrategicObjectiveBindingRow[];
  initiativeObjectiveBindings: readonly InitiativeObjectiveBindingRow[];
  realizationCommitments?: readonly RealizationCommitmentRow[];
}>;

export type GovernedGraphReadResult =
  | Readonly<{ ok: true; edges: readonly GovernedGraphEdge[] }>
  | Readonly<{
      ok: false;
      reasonCode:
        | 'stale_structure'
        | 'scope_mismatch'
        | 'unapproved_source'
        | 'missing_citation';
      message: string;
    }>;

/**
 * The thin reader contract the evaluator's edge leaves resolve against (charter
 * §5.3). `readEdges` returns STRUCTURE + citations only — never a metric value.
 */
export type GovernedGraphReaderPort = Readonly<{
  readEdges: (
    edgeType: OntologyRelationshipKindDto,
    scope: GovernedGraphReaderScope,
  ) => GovernedGraphReadResult;
}>;

// ---- Node id conventions (typed node addressing, matches the projection domains) ----

export function metricNodeId(metricKey: string): string {
  return `metric:${metricKey}`;
}
export function objectiveNodeId(objectiveType: string): string {
  return `objective:${objectiveType}`;
}
export function initiativeNodeId(initiativeId: string): string {
  return `initiative:${initiativeId}`;
}
export function commitmentNodeId(commitmentId: string): string {
  return `commitment:${commitmentId}`;
}
export function ownerNodeId(ownerUserId: string): string {
  return `owner:${ownerUserId}`;
}

/**
 * Live-reprojection of the touched subgraph from the relational bindings into typed
 * structural edges (realization B). Emits, per objective:
 *   - `MEASURES`        metric      → objective   (StrategicObjectiveBinding)
 *   - `ADVANCES`        initiative  → objective   (InitiativeObjectiveBinding)
 *   - `ACCOUNTABLE_FOR` owner       → objective   (StrategicObjectiveBinding.ownerUserId;
 *                                                  the projected `owner_accountable_for_objective` edge)
 *   - `MOVES`           commitment  → metric      (realization-commitment DTO)
 *   - `REFERENCES`      commitment  → initiative  (governed propagation binding, when present)
 *
 * Every edge is stamped with `scope.asOf` (as-of pinning §5.2) and carries the
 * source artifact citation. No numeric value is ever attached.
 */
export function reprojectGovernedStructure(
  input: GovernedGraphReprojectionInput,
  scope: GovernedGraphReaderScope,
): GovernedGraphReadResult {
  const sourceRows = [
    ...input.strategicObjectiveBindings,
    ...input.initiativeObjectiveBindings,
    ...(input.realizationCommitments ?? []),
  ];
  const rowWithoutId = sourceRows.find((row) => !isNonBlank(row.id));
  if (rowWithoutId) {
    return {
      ok: false,
      reasonCode: 'missing_citation',
      message: 'source row id is required before emitting a governed edge',
    };
  }
  const realizationWithoutSource = (input.realizationCommitments ?? []).find(
    (row) => !isNonBlank(row.sourceArtifactRef),
  );
  if (realizationWithoutSource) {
    return {
      ok: false,
      reasonCode: 'missing_citation',
      message: `realization source row ${realizationWithoutSource.id} has no source artifact reference`,
    };
  }
  const foreignRow = sourceRows.find((row) => !sourceRowMatchesScope(row, scope));
  if (foreignRow) {
    return {
      ok: false,
      reasonCode: 'scope_mismatch',
      message: `source row ${foreignRow.id} does not match the immutable evaluation scope`,
    };
  }
  const unapprovedRow = sourceRows.find((row) => row.approvedAt === null);
  if (unapprovedRow) {
    return {
      ok: false,
      reasonCode: 'unapproved_source',
      message: `source row ${unapprovedRow.id} is not approved and cannot emit a governed edge`,
    };
  }
  const edges: GovernedGraphEdge[] = [];

  for (const sob of input.strategicObjectiveBindings) {
    const objectiveId = objectiveNodeId(sob.objectiveType);
    const asOf = sob.asOf;
    const rowScope = sourceOwnedScope(sob, scope);
    edges.push({
      fromId: metricNodeId(sob.metricKey),
      toId: objectiveId,
      edgeType: 'MEASURES',
      fromKind: 'metric',
      toKind: 'objective',
      sourceArtifactRef: `StrategicObjectiveBinding:${sob.id}`,
      sourceBindingId: sob.id,
      sourceApprovedAt: sob.approvedAt,
      sourceGeneration: sob.sourceGeneration ?? null,
      asOf,
      scope: rowScope,
    });
    if (sob.ownerUserId) {
      edges.push({
        fromId: ownerNodeId(sob.ownerUserId),
        toId: objectiveId,
        edgeType: 'ACCOUNTABLE_FOR',
        fromKind: 'owner',
        toKind: 'objective',
        sourceArtifactRef: `StrategicObjectiveBinding:${sob.id}:owner`,
        sourceBindingId: sob.id,
        sourceApprovedAt: sob.approvedAt,
        sourceGeneration: sob.sourceGeneration ?? null,
        asOf,
        scope: rowScope,
      });
    }
  }

  for (const iob of input.initiativeObjectiveBindings) {
    const rowScope = sourceOwnedScope(iob, scope);
    edges.push({
      fromId: initiativeNodeId(iob.initiativeId),
      toId: objectiveNodeId(iob.objectiveType),
      edgeType: 'ADVANCES',
      fromKind: 'initiative',
      toKind: 'objective',
      sourceArtifactRef: `InitiativeObjectiveBinding:${iob.id}`,
      sourceBindingId: iob.id,
      sourceApprovedAt: iob.approvedAt,
      weightPct: iob.weightPct,
      sourceGeneration: iob.sourceGeneration ?? null,
      asOf: iob.asOf,
      scope: rowScope,
    });
  }

  for (const commitment of input.realizationCommitments ?? []) {
    const lineage = {
      sourceArtifactRef: commitment.sourceArtifactRef,
      sourceBindingId: commitment.id,
      sourceApprovedAt: commitment.approvedAt,
      sourceGeneration: commitment.sourceGeneration ?? null,
      asOf: commitment.asOf,
      scope: sourceOwnedScope(commitment, scope),
    };
    edges.push({
      ...lineage,
      fromId: commitmentNodeId(commitment.commitmentId),
      toId: metricNodeId(commitment.metricKey),
      edgeType: 'MOVES',
      fromKind: 'commitment',
      toKind: 'metric',
    });
    if (isNonBlank(commitment.initiativeId)) {
      edges.push({
        ...lineage,
        fromId: commitmentNodeId(commitment.commitmentId),
        toId: initiativeNodeId(commitment.initiativeId),
        edgeType: 'REFERENCES',
        fromKind: 'commitment',
        toKind: 'initiative',
      });
    }
    const ownerId = commitment.ownerPosition
      ? ownerNodeId(`position:${commitment.ownerPosition.orgStateId}:${commitment.ownerPosition.positionId}`)
      : isNonBlank(commitment.ownerUserId) ? ownerNodeId(commitment.ownerUserId) : null;
    if (ownerId) {
      edges.push({
        ...lineage,
        ownerPosition: commitment.ownerPosition,
        fromId: ownerId,
        toId: commitmentNodeId(commitment.commitmentId),
        edgeType: 'ACCOUNTABLE_FOR',
        fromKind: 'owner',
        toKind: 'commitment',
      });
    }
  }

  return { ok: true, edges: Object.freeze(edges) };
}

function isNonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function sourceRowMatchesScope(
  row: Readonly<{ tenantId: string; strategyId: string; asOf: string }>,
  scope: GovernedGraphReaderScope,
): boolean {
  return (
    row.tenantId === scope.tenantId &&
    row.strategyId === scope.strategyId &&
    row.asOf === scope.asOf
  );
}

function sourceOwnedScope(
  row: Readonly<{ tenantId: string; strategyId: string; asOf: string }>,
  scope: GovernedGraphReaderScope,
): GovernedGraphReaderScope {
  return {
    tenantId: row.tenantId,
    strategyId: row.strategyId,
    asOf: row.asOf,
    industryProfileId: scope.industryProfileId,
    companyMappingId: scope.companyMappingId,
  };
}

/**
 * Structure-generation guard for materialized reads (realization A). Every edge
 * must carry a `sourceGeneration` and it must equal the current source generation;
 * on any divergence the read fails closed `stale_structure` and the caller falls
 * back to live reprojection of that subgraph. Under live reprojection (B) there is
 * no generation skew, so this is a no-op unless a caller opts into materialized
 * reads by passing `materialized: true`.
 */
export function assertStructureGeneration(
  edges: readonly GovernedGraphEdge[],
  currentSourceGeneration: string | null,
  materialized: boolean,
): GovernedGraphReadResult {
  if (!materialized) return { ok: true, edges };
  for (const edge of edges) {
    if (edge.sourceGeneration === null || edge.sourceGeneration !== currentSourceGeneration) {
      return {
        ok: false,
        reasonCode: 'stale_structure',
        message: `materialized projection edge ${edge.sourceArtifactRef} is behind source generation ${currentSourceGeneration}`,
      };
    }
  }
  return { ok: true, edges };
}

/**
 * As-of pinning guard (§5.2). A chain that mixes two as-ofs across hops is a
 * `scope_mismatch` withhold — no cross-as-of blending, mirroring the variance
 * family's frame guard. Returns the reason code, or `null` when consistent.
 */
export function assertChainAsOfConsistent(
  edges: readonly GovernedGraphEdge[],
  scope: GovernedGraphReaderScope,
): 'scope_mismatch' | null {
  for (const edge of edges) {
    if (edge.asOf !== scope.asOf) return 'scope_mismatch';
  }
  return null;
}

/**
 * Build the reader port over an already-reprojected edge set (realization B). The
 * port answers `readEdges(edgeType, scope)`, fencing every read through the as-of
 * pinning guard.
 */
export function buildGovernedGraphReaderPort(
  edges: readonly GovernedGraphEdge[],
): GovernedGraphReaderPort {
  return {
    readEdges: (edgeType, scope) => {
      if (edges.some((edge) => edge.edgeType === edgeType && !sameGraphScope(edge.scope, scope))) {
        return {
          ok: false,
          reasonCode: 'scope_mismatch',
          message: 'requested graph scope does not match the source-owned edge scope',
        };
      }
      const scoped = edges.filter(
        (edge) => edge.edgeType === edgeType && sameGraphScope(edge.scope, scope),
      );
      const mismatch = assertChainAsOfConsistent(scoped, scope);
      if (mismatch) {
        return { ok: false, reasonCode: mismatch, message: 'chain mixes as-ofs across hops' };
      }
      return { ok: true, edges: scoped };
    },
  };
}

// ---- Bridge: reader structure → evaluator edge leaf (`ports.readGraphEdge`) ----

function termValue(term: InferenceEdgeLiteral['from'], bindings: InferenceBindings): unknown {
  if (term.kind === 'constant') return term.value;
  return bindings[term.name];
}

/**
 * Adapt a reprojected edge set into the evaluator's `readGraphEdge` port. For an
 * edge literal with one side bound (from `bindings` or a constant), enumerate the
 * reprojected edges of that type matching the bound endpoint and bind the free
 * variable to the other endpoint — carrying the edge's citation as the hop's
 * provenance. Structure only; no value is ever bound to a numeric output here.
 */
export function buildInferenceGraphEdgePort(
  edges: readonly GovernedGraphEdge[],
): (
  literal: InferenceEdgeLiteral,
  bindings: InferenceBindings,
  scope: InferenceEvaluationScope,
) => readonly InferenceReadResult[] {
  return (literal, bindings, scope) => {
    const results: InferenceReadResult[] = [];
    const fromVal = termValue(literal.from, bindings);
    const toVal = termValue(literal.to, bindings);
    for (const edge of edges) {
      if (edge.edgeType !== literal.relationshipKind) continue;
      if (!sameGraphScope(edge.scope, scope)) continue;
      if (fromVal !== undefined && fromVal !== null && String(fromVal) !== edge.fromId) continue;
      if (toVal !== undefined && toVal !== null && String(toVal) !== edge.toId) continue;
      const newBindings: Record<string, string | number | boolean | null> = {};
      if (literal.from.kind === 'variable' && (fromVal === undefined || fromVal === null)) {
        newBindings[literal.from.name] = edge.fromId;
      }
      if (literal.to.kind === 'variable' && (toVal === undefined || toVal === null)) {
        newBindings[literal.to.name] = edge.toId;
      }
      results.push({
        bindings: Object.freeze(newBindings),
        scope: edge.scope,
        citation: {
          sourceKind: 'graph_edge',
          sourceRef: edge.sourceArtifactRef,
          asOf: edge.asOf,
          bindingId: edge.sourceBindingId,
          approvedAt: edge.sourceApprovedAt ?? undefined,
          weightPct: edge.weightPct,
        },
      });
    }
    return Object.freeze(results);
  };
}

function sameGraphScope(
  left: GovernedGraphReaderScope,
  right: GovernedGraphReaderScope,
): boolean {
  return (
    left.tenantId === right.tenantId &&
    left.strategyId === right.strategyId &&
    left.asOf === right.asOf &&
    (left.industryProfileId ?? null) === (right.industryProfileId ?? null) &&
    (left.companyMappingId ?? null) === (right.companyMappingId ?? null)
  );
}
